import { cx } from '@linaria/core';
import { useRef, useId, useState, useCallback } from 'react';
import { getGlossaryWord } from '@pm2/api';
import { floatingButtonSelectors, pm2PlaceholderSelectors } from '@pm2/ui-layout/selectors';
import { useAppContext } from '@provider/app-context';
import { Overlay, FocusOn, speechSelectors } from '@ui/kit';
import { printHtmlToJsx } from '@utils/ast';
import { useToggleState, useClickOutsideListener, useLoaderState } from '@utils/react';
import { togglerCls, overlayCls, definitionCls, closeBtnCls, loadingEllipsisCls } from './glossary-word.styles.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';

/**
 * wrap glossary words in a container that'll trigger a display of the glossary
 * word in a tooltip upon hover.
 */
const GlossaryWord = ({
  id,
  children
}) => {
  const buttonRef = useRef(null);
  const overlayRef = useRef(null);
  const {
    definition,
    showLoader,
    eventHandlers
  } = useDefinition(id);
  const overlayId = `glossaryWord_${useId()}`;
  const [isActive, actions] = useToggleState();
  const content = definition === undefined || showLoader ? /*#__PURE__*/jsxs(Fragment, {
    children: ["Indl\xE6ser", /*#__PURE__*/jsx("span", {
      className: loadingEllipsisCls,
      children: "."
    })]
  }) : definition;
  const speechSynthesisButton = document.querySelector(floatingButtonSelectors.speechSynthesis());
  useClickOutsideListener(() => actions.disable(), [buttonRef, overlayRef], {
    disabled: !isActive,
    useCapturePhase: true
  });
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("button", {
      ...eventHandlers,
      className: cx("gGlossaryWord__word", togglerCls),
      "data-word-id": id,
      "aria-controls": overlayId,
      "aria-expanded": isActive,
      ref: buttonRef,
      onClick: () => actions.toggle()
      // prevent FocusOn onClickOutside from running when clicking this button
      // to ensure that the onClick toggle handler triggers the expected
      // outcome of toggling the button on/off
      ,
      onMouseDown: evt => evt.stopPropagation(),
      onTouchStart: evt => evt.stopPropagation()
      // Firefox requires a style attribute of userSelect to be
      // set for polly to work correctly.
      // See ADO #108360
      ,
      style: {
        userSelect: "auto"
      },
      children: children
    }), /*#__PURE__*/jsx(Overlay, {
      className: overlayCls,
      placement: "bottom-start",
      active: isActive,
      withArrow: false,
      containerRef: buttonRef,
      mountIn: document.querySelector(pm2PlaceholderSelectors.postPageRouter()),
      children: /*#__PURE__*/jsx(FocusOn, {
        enabled: isActive,
        onEscapeKey: () => actions.disable(),
        scrollLock: false,
        focusLock: true,
        autoFocus: true,
        noIsolation: true,
        allowPinchZoom: true,
        returnFocus: {
          preventScroll: true
        },
        preventScrollOnFocus: true,
        shards: speechSynthesisButton ? [speechSynthesisButton] : undefined,
        children: /*#__PURE__*/jsxs("div", {
          id: overlayId,
          ref: overlayRef,
          className: definitionCls,
          children: [/*#__PURE__*/jsxs("dl", {
            className: speechSelectors.enableSpeech.className,
            children: [/*#__PURE__*/jsx("dt", {
              children: children
            }), /*#__PURE__*/jsx("dd", {
              children: content
            })]
          }), /*#__PURE__*/jsx("button", {
            className: closeBtnCls,
            onClick: () => actions.disable(),
            "aria-label": "Luk ordforklaring",
            children: /*#__PURE__*/jsx("span", {
              "data-cross": true
            })
          })]
        })
      })
    })]
  });
};

/**
 * fetch the definition of the glossary word, when the user moves the cursor
 * over the element.
 */
function useDefinition(id) {
  const portalId = useAppContext(state => state.product?.id);
  const [definition, setDefinition] = useState(undefined);
  const [isLoading, setLoading] = useState(false);
  const showLoader = useLoaderState(isLoading);
  const handleLoadDefinition = useCallback(async () => {
    if (!portalId) {
      // bail out if no portal was configured, as we then have no way to fetch
      // data
      return;
    }
    if (isLoading || definition !== undefined) {
      // bail out if definition is already requested
      return;
    }
    try {
      setLoading(true);
      setDefinition(printHtmlToJsx(await getGlossaryWord(portalId, id, {
        previewMode: false
      })));
    } finally {
      setLoading(false);
    }
  }, [definition, id, isLoading, portalId]);
  return {
    definition,
    showLoader: showLoader.current,
    eventHandlers: {
      onPointerEnter: handleLoadDefinition,
      onFocus: handleLoadDefinition
    }
  };
}

export { GlossaryWord, useDefinition };
//# sourceMappingURL=glossary-word.js.map
